import {
  Component,
  effect,
  inject,
  model,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { EnrichedPack, Pack } from 'src/app/shared/model/pack';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { Company } from 'src/app/user/model/user';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { PackService } from 'src/app/admin/services/pack.service';
import { DisplayPackOptionsComponent } from 'src/app/shared/components/display-pack-options/display-pack-options.component';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-manage-company-pack',
  imports: [
    ReactiveFormsModule,
    CheckboxModule,
    InputNumberModule,
    SelectModule,
    DialogModule,
    ButtonModule,
    DisplayPackOptionsComponent,
    CommonModule,
    TooltipModule,
  ],
  templateUrl: './manage-company-pack.component.html',
})
export class ManageCompanyPackComponent implements OnInit, OnChanges, OnDestroy {
  packService = inject(PackService);
  notificationService = inject(NotificationService);
  fb = inject(FormBuilder);
  company = model.required<Company>();
  packForm = this.fb.nonNullable.group({
    id: ['', Validators.required],
    tripLimit: [0],
    userLimit: [0],
    extraUserLimit: [1],
    bulkUpload: [0],
    exportPDF: [false],
    exportExcel: [false],
    clientFilter: [false],
    comparisonGraph: [false],
  });
  visible: boolean = false;
  loading: boolean = false;

  pack?: EnrichedPack;
  packOptions: Pack[] = [];
  defaultPackOptions?: EnrichedPack;
  packValueChangeSubscription?: Subscription;
  usedTrips = 0;
  usedUsers = 0;
  constructor() {
    effect(() => {
      this.packOptions = this.packService.packs() ?? [];
      this.setDefaultPackOptions(this.company().pack.id);
    });
  }

  ngOnInit() {
    this.pack = this.company().pack;
    this.populateForm();
    this.packValueChangeSubscription = this.packForm
      .get('id')
      ?.valueChanges.subscribe((id) => {
        this.setDefaultPackOptions(id);
        this.resetToDefaultPackOptions();
      });
    this.usedTrips = this.company()._count?.trips ?? 0;
    this.usedUsers = this.company()._count?.users ?? 0;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['company']) {
      const idControl = this.packForm.get('id');
      if (this.company().size) {
        idControl?.enable();
      } else {
        idControl?.disable();
      }
    }
  }
  ngOnDestroy(): void {
    this.packValueChangeSubscription?.unsubscribe();
  }
  populateForm() {
    const value = {
      ...this.pack,
      bulkUpload: this.pack?.bulkUpload ?? 0,
    };
          this.packForm.patchValue(
        {
          ...value,
          extraUserLimit: value.extraUserLimit ?? undefined,
        },
        { emitEvent: false },
      );
      if (this.pack?.id) this.setDefaultPackOptions(this.pack.id);
  }
  
  setDefaultPackOptions(packId: string) {
    const associatedPack = this.packOptions.find((pack) => pack.id === packId);
    if (associatedPack) {
      let packSizeConfig;
      const hasSingleConfig = associatedPack.packSizeConfig.length === 1;
      if (hasSingleConfig) {
        packSizeConfig = associatedPack.packSizeConfig[0];
      } else {
        packSizeConfig = associatedPack.packSizeConfig.find(
          (packSizeConfig) => packSizeConfig.companySize === this.company().size,
        );
        if (!packSizeConfig) {
          console.warn(
            'No pack config found for company size:',
            this.company().size,
          );
          return;
        }
      }

      this.defaultPackOptions = {
        ...associatedPack,
        tripLimit: packSizeConfig.tripLimit,
        userLimit: packSizeConfig.userLimit,
        extraUserLimit: packSizeConfig.extraUserLimit,
        bulkUpload: packSizeConfig.bulkUpload,
      };
    }
  }
  showDialog() {
    this.visible = true;
  }
  closeDialog() {
    this.populateForm();
    this.visible = false;
  }
  submit() {
    if (!this.pack?.id) return;
    this.loading = true;
    const values = this.packForm.getRawValue();
    const packId = values.id;
    const overrides = { ...values };
    this.packService
      .updateCompanyPack(packId, overrides, this.company().id!)
      .subscribe({
        next: (pack) => {
          this.pack = pack;
          this.company.update((company) => ({
            ...company,
            pack,
            packId: pack.id,
          }));
          this.loading = false;
          this.notificationService.success('pack successfully updated');
          this.closeDialog();
        },
        error: () => {
          this.notificationService.error('error while updating pack');
          this.loading = false;
        },
      });
  }

  resetToDefaultPackOptions() {
    const values = this.defaultPackOptions;
    if (!values) {
      return;
    }
    this.packForm.patchValue(
      {
        ...values,
        extraUserLimit: values.extraUserLimit ?? undefined,
      },
      { emitEvent: false },
    );
  }
}
